(() => {
  // -------------------------
  // Utilities
  // -------------------------
  
  const getRootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getConfig = () => {
    const el = document.getElementById('VariantSegmentConfig');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch { return null; }
  };

  const getOriginalHandle = () => {
    const match = location.pathname.match(/\/collections\/([^\/]+)/);
    return match ? match[1] : null;
  };

  // -------------------------
  // Loading state management
  // -------------------------
  const setClass = (cls, on) => document.documentElement.classList.toggle(cls, !!on);
  const setGridLoading = (on) => setClass('variant-segment-grid-loading', on);
  const unhideInitial = () => setClass('variant-segment-initial-loading', false);

  let safetyTimer = null;
  const startSafetyTimer = (ms) => {
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(unhideInitial, Math.max(0, ms || 0));
  };
  const stopSafetyTimer = () => clearTimeout(safetyTimer);

  // -------------------------
  // Fetch interceptor for third-party infinite scroll
  // -------------------------
  let fetchInterceptorActive = false;
  let interceptFromHandle = null;
  let interceptToHandle = null;

  const setupFetchInterceptor = (toHandle) => {
    const fromHandle = getOriginalHandle();
    if (!fromHandle || !toHandle || fromHandle === toHandle) return;
    
    interceptFromHandle = fromHandle;
    interceptToHandle = toHandle;
    
    if (fetchInterceptorActive) return;
    fetchInterceptorActive = true;
    
    const originalFetch = window.fetch;
    window.fetch = function(input, init) {
      let url = input instanceof Request ? input.url : String(input);
      
      if (interceptToHandle && url.includes(`/collections/${interceptFromHandle}`)) {
        url = url.replace(
          `/collections/${interceptFromHandle}`,
          `/collections/${interceptToHandle}`
        );
        if (input instanceof Request) {
          input = new Request(url, input);
        } else {
          input = url;
        }
      }
      
      return originalFetch.call(this, input, init);
    };
  };

  // -------------------------
  // Handle building
  // -------------------------
  const parseSegment = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    try {
      const obj = JSON.parse(raw);
      const id = obj?.segment_id?.toString().trim();
      return id ? { segmentId: id, raw: obj } : null;
    } catch { return null; }
  };

  const buildHandle = (segmentId, collectionId) => {
    const sid = segmentId?.toString().trim();
    const cid = collectionId?.toString().trim();
    return (sid && cid) ? `${sid}_${cid}` : '';
  };

  const pickHandleFromStorage = (config) => {
    const key = config?.storageKey || 'variant_segment';
    let raw = '';
    try { raw = localStorage.getItem(key) || ''; } catch {}
    const parsed = parseSegment(raw);
    return parsed ? buildHandle(parsed.segmentId, config?.currentCollectionId) : '';
  };

  // -------------------------
  // DOM helpers
  // -------------------------
  const findMainSectionId = () => {
    const el = document.querySelector('[id^="shopify-section-"][id$="__main"]');
    return el ? el.id.replace(/^shopify-section-/, '') : null;
  };

  const findItemsContainer = (root) => {
    const selectors = [
      '#product-grid', '[data-product-grid]', 'ul.product-grid', '.product-grid',
      '[id*="ProductGridContainer"] ul', '[id*="ProductGridContainer"] .product-grid'
    ];
    for (const sel of selectors) {
      const el = root.querySelector(sel);
      if (el?.children) return el;
    }
    return null;
  };

  const looksLikeLiquidError = (html) => {
    if (!html) return true;
    const s = html.toLowerCase();
    return s.includes('liquid error') || s.includes('not paginateable');
  };

  // -------------------------
  // Preserve collection header
  // -------------------------
  const getCollectionHeader = () => {
    const header = document.querySelector('.collection__header');
    return header ? header.cloneNode(true) : null;
  };

  const restoreCollectionHeader = (savedHeader) => {
    if (!savedHeader) return;
    const currentHeader = document.querySelector('.collection__header');
    if (currentHeader) {
      currentHeader.replaceWith(savedHeader.cloneNode(true));
    }
  };

  // -------------------------
  // URL building
  // -------------------------
  const buildSectionsUrl = (handle, sectionIds, extraParams) => {
    const url = new URL(`${getRootPath()}collections/${encodeURIComponent(handle)}`, location.origin);
    
    new URLSearchParams(location.search).forEach((v, k) => {
      if (k !== 'sections' && k !== 'section_id') url.searchParams.append(k, v);
    });
    
    extraParams?.forEach((v, k) => {
      if (k !== 'sections' && k !== 'section_id') url.searchParams.set(k, v);
    });
    
    url.searchParams.set('sections', sectionIds.join(','));
    return url.toString();
  };

  // -------------------------
  // Collection existence check (cached)
  // -------------------------
  const existsCache = new Map();

  const checkCollectionExists = async (handle) => {
    if (!handle) return false;
    if (existsCache.has(handle)) return existsCache.get(handle);
    
    try {
      const url = `${getRootPath()}collections/${encodeURIComponent(handle)}.json`;
      const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const ok = res?.ok ?? false;
      existsCache.set(handle, ok);
      return ok;
    } catch {
      existsCache.set(handle, false);
      return false;
    }
  };

  // -------------------------
  // Pagination URL rewriter
  // -------------------------
  let paginationObserver = null;

  const setupPaginationRewriter = (handle) => {
    paginationObserver?.disconnect();
    if (!handle) return;

    const rewrite = () => {
      const links = document.querySelectorAll('.pagination a[href*="/collections/"]');
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (!href.includes(`/collections/${handle}`)) {
          try {
            const url = new URL(href, location.origin);
            url.pathname = url.pathname.replace(
              /\/collections\/[^\/]+/,
              `/collections/${encodeURIComponent(handle)}`
            );
            link.setAttribute('href', url.toString());
          } catch {}
        }
      });
    };

    rewrite();

    const pagination = document.querySelector('.pagination');
    if (pagination) {
      paginationObserver = new MutationObserver(rewrite);
      paginationObserver.observe(pagination, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    }
  };

  // -------------------------
  // Interaction detection
  // -------------------------
  const hasCollectionParams = (u) => {
    if (u.searchParams.has('sort_by') || u.searchParams.has('page') || u.searchParams.has('limit')) return true;
    for (const k of u.searchParams.keys()) if (k.startsWith('filter.')) return true;
    return false;
  };

  const isFacetControl = (el) => {
    if (!el?.closest) return false;
    const facetSelectors = ['.facets', 'facet-filters-form', 'facet-remove-component', 'facet-link', '.active-facets', '.removable-facet'];
    return facetSelectors.some(s => el.closest(s)) || el.classList?.contains('facets__clear-all') || el.classList?.contains('facets__remove');
  };

  const getValuePickerSelection = (e) => {
    const btn = e.target?.closest?.('[data-action="select-value"][data-value]');
    if (!btn) return null;
    
    const value = btn.getAttribute('data-value');
    const picker = btn.closest('.value-picker');
    const pickerId = picker?.getAttribute('id');
    
    if (pickerId === 'sort-by-selector') {
      return { param: 'sort_by', value };
    }
    if (pickerId === 'display-by-selector') {
      return { param: 'limit', value };
    }
    
    return null;
  };

  const paramsFromForm = (form) => {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (typeof v === 'string' && v) params.append(k, v);
    }
    return params;
  };

  const isPaginationLink = (url) => {
    const newPage = url.searchParams.get('page');
    const currentPage = new URLSearchParams(location.search).get('page') || '1';
    return newPage && newPage !== currentPage;
  };

  // -------------------------
  // Core state
  // -------------------------
  let controlsBound = false;
  let swapInFlight = null;
  let cachedMainId = null;
  let swapEnabled = false;
  let desiredHandle = '';
  let doSwapRef = null;
  let readyResolve = null;
  const ready = new Promise(r => readyResolve = r);

  // -------------------------
  // Public API
  // -------------------------
  
  window.VariantPrepareSwap = () => {
    const config = getConfig() || {};
    const maxMs = Number(config.initialHideMaxMs) || 2000;
    setClass('variant-segment-initial-loading', true);
    startSafetyTimer(maxMs);
  };

  window.VariantSwapCollection = async (segmentId) => {
    segmentId='e9c5eff0-c5cc-4cc4-9a22-bbc7e510d30f'
    await ready;
    const config = getConfig() || {};
    
    desiredHandle = segmentId 
      ? buildHandle(segmentId, config.currentCollectionId)
      : pickHandleFromStorage(config);

    if (!desiredHandle) {
      setGridLoading(false);
      unhideInitial();
      stopSafetyTimer();
      return false;
    }

    setGridLoading(true);
    try {
      const ok = await doSwapRef();
      if (ok) swapEnabled = true;
      return !!ok;
    } finally {
      setGridLoading(false);
      unhideInitial();
      stopSafetyTimer();
    }
  };

  // -------------------------
  // Boot
  // -------------------------
  const boot = async () => {
    const config = getConfig() || {};
    
    if (document.documentElement.classList.contains('variant-segment-initial-loading')) {
      const maxMs = Number(config.initialHideMaxMs) || 2000;
      startSafetyTimer(maxMs);
    }
    
    cachedMainId = findMainSectionId();
    if (!cachedMainId) {
      unhideInitial();
      stopSafetyTimer();
      return;
    }
    
    const doSwap = async () => {
      if (!desiredHandle) return false;
      if (!(await checkCollectionExists(desiredHandle))) {
        console.log('no segmented collection exists');
        return false;
      }
      if (swapInFlight) return swapInFlight;
      
      swapInFlight = (async () => {
        try {
          // Save original header before swap
          const savedHeader = getCollectionHeader();
          
          const url = buildSectionsUrl(desiredHandle, [cachedMainId]);
          const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          
          if (!res.ok) return false;
          
          const data = await res.json();
          const html = data[cachedMainId];
          
          if (!html || looksLikeLiquidError(html)) return false;

          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const newSection = tmp.querySelector(`#shopify-section-${CSS.escape(cachedMainId)}`);
          
          if (!newSection) return false;

          document.getElementById(`shopify-section-${cachedMainId}`)?.replaceWith(newSection);
          
          // Restore original header after swap
          restoreCollectionHeader(savedHeader);
          
          document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: cachedMainId } }));
          setupPaginationRewriter(desiredHandle);
          setupFetchInterceptor(desiredHandle);
          
          return true;
        } catch {
          return false;
        }
      })();

      try { return await swapInFlight; }
      finally { swapInFlight = null; }
    };

    doSwapRef = doSwap;
    readyResolve?.();

    // -------------------------
    // Event bindings (once)
    // -------------------------
    if (controlsBound) return;
    controlsBound = true;

    const getMainRoot = () => cachedMainId ? document.getElementById(`shopify-section-${cachedMainId}`) : null;
    
    const isInMain = (e) => {
      const root = getMainRoot();
      if (!root) return false;
      const path = e.composedPath?.() || [];
      return path.some(n => n instanceof Node && root.contains(n)) || (e.target instanceof Node && root.contains(e.target));
    };

    const handleInteraction = async (newParams, scrollToTop = false) => {
      const newUrl = new URL(location.href);
      newUrl.search = newParams.toString();
      history.pushState({}, '', newUrl.toString());
      
      setGridLoading(true);
      try { 
        await doSwap();
        if (scrollToTop) {
          const mainEl = getMainRoot();
          if (mainEl) {
            mainEl.scrollIntoView({ behavior: 'instant', block: 'start' });
          } else {
            window.scrollTo(0, 0);
          }
        }
      }
      finally { setGridLoading(false); }
    };

    // Click handler
    window.addEventListener('click', async (e) => {
      if (!swapEnabled || !isInMain(e)) return;

      const pickerSelection = getValuePickerSelection(e);
      if (pickerSelection) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const p = new URLSearchParams(location.search);
        p.set(pickerSelection.param, pickerSelection.value);
        p.delete('page');
        return handleInteraction(p, false);
      }

      const dataUrlEl = e.target?.closest?.('[data-url]');
      if (dataUrlEl) {
        const raw = dataUrlEl.getAttribute('data-url');
        try {
          const u = new URL(raw, location.origin);
          if (u.pathname.startsWith('/collections/')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return handleInteraction(new URLSearchParams(u.search), isPaginationLink(u));
          }
        } catch {}
      }

      const a = e.target?.closest?.('a[href]');
      if (!a) return;
      
      try {
        const u = new URL(a.getAttribute('href'), location.origin);
        if (u.pathname.startsWith('/collections/') && (hasCollectionParams(u) || isFacetControl(a))) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return handleInteraction(new URLSearchParams(u.search), isPaginationLink(u));
        }
      } catch {}
    }, true);

    // Submit handler
    window.addEventListener('submit', async (e) => {
      if (!swapEnabled) return;
      const form = e.target?.closest?.('form');
      const root = getMainRoot();
      if (!form || (root && !root.contains(form)) || form.method?.toLowerCase() !== 'get') return;

      e.preventDefault();
      e.stopImmediatePropagation();
      const p = paramsFromForm(form);
      p.delete('page');
      return handleInteraction(p, false);
    }, true);

    // Change handler
    window.addEventListener('change', async (e) => {
      if (!swapEnabled) return;
      const el = e.target;
      const root = getMainRoot();
      if (!el?.getAttribute || (root && !root.contains(el))) return;

      const name = el.getAttribute('name') || '';
      if (name !== 'sort_by' && !name.startsWith('filter.')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      const form = el.closest('form');
      const p = form ? paramsFromForm(form) : new URLSearchParams(location.search);
      p.delete('page');
      return handleInteraction(p, false);
    }, true);

    // Popstate
    window.addEventListener('popstate', async () => {
      if (!swapEnabled) return;
      setGridLoading(true);
      try { 
        await doSwap();
        const mainEl = getMainRoot();
        if (mainEl) {
          mainEl.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }
      finally { setGridLoading(false); }
    });
  };

  boot().catch(() => {
    setGridLoading(false);
    unhideInitial();
    stopSafetyTimer();
  });
})();